(() => {
  'use strict';

  const root = document.documentElement;
  if (root.dataset.caesarUsAccessibility !== 'true') return;

  const setAttribute = (element, name, value) => {
    if (element && element.getAttribute(name) !== value) element.setAttribute(name, value);
  };

  const removeAttribute = (element, name) => {
    if (element?.hasAttribute(name)) element.removeAttribute(name);
  };

  const textFrom = (element) => (element?.textContent || '').replace(/\s+/g, ' ').trim();

  const currentPageNumber = () => {
    const current = document.querySelector('.pagination__item--current[aria-current="page"]');
    const value = Number.parseInt(textFrom(current), 10);
    return Number.isFinite(value) ? value : 1;
  };

  const pageNumberFromLink = (link) => {
    try {
      const url = new URL(link.href, window.location.href);
      const value = Number.parseInt(url.searchParams.get('page') || '1', 10);
      return Number.isFinite(value) ? value : null;
    } catch {
      return null;
    }
  };

  const fixSortControls = () => {
    document.querySelectorAll('select#SortBy, select[name="sort_by"]').forEach((select) => {
      if (!select.getAttribute('aria-label') && !select.getAttribute('aria-labelledby')) {
        const label = select.id ? document.querySelector(`label[for="${CSS.escape(select.id)}"]`) : null;
        setAttribute(select, 'aria-label', textFrom(label) || 'Sort products');
      }
    });
  };

  const fixPagination = () => {
    document.querySelectorAll('.pagination__list').forEach((list) => {
      const hasInvalidDirectChild = [...list.children].some((child) => child.tagName !== 'LI');
      if (hasInvalidDirectChild) {
        setAttribute(list, 'role', 'group');
        setAttribute(list, 'aria-label', 'Pagination pages');
        [...list.children].forEach((child) => setAttribute(child, 'role', 'presentation'));
      }
    });

    const currentPage = currentPageNumber();
    document.querySelectorAll('a.ss__pagination__link, a.pagination__item-arrow').forEach((link) => {
      if (link.getAttribute('aria-label') || textFrom(link)) return;
      const targetPage = pageNumberFromLink(link);
      if (targetPage && targetPage < currentPage) {
        setAttribute(link, 'aria-label', 'Previous page');
      } else if (targetPage && targetPage > currentPage) {
        setAttribute(link, 'aria-label', 'Next page');
      } else if (targetPage) {
        setAttribute(link, 'aria-label', `Go to page ${targetPage}`);
      } else {
        setAttribute(link, 'aria-label', 'Pagination link');
      }
    });
  };

  const fixNestedShopLinks = () => {
    document.querySelectorAll('button.shopnow-btn').forEach((button) => {
      const links = [...button.children].filter((child) => child.matches('a[href]'));
      if (links.length !== 1) return;
      const replacement = links[0].cloneNode(true);
      button.classList.forEach((className) => replacement.classList.add(className));
      if (button.id && !replacement.id) replacement.id = button.id;
      if (button.getAttribute('style')) replacement.setAttribute('style', button.getAttribute('style'));
      [...button.attributes].forEach((attribute) => {
        if (attribute.name.startsWith('data-') && !replacement.hasAttribute(attribute.name)) {
          replacement.setAttribute(attribute.name, attribute.value);
        }
      });
      button.replaceWith(replacement);
    });
  };

  const fixProductGalleries = () => {
    document.querySelectorAll('#productSliderGallery, #productSliderThumbs').forEach((gallery) => {
      setAttribute(gallery, 'role', 'group');
      setAttribute(gallery, 'aria-roledescription', 'carousel');
      setAttribute(
        gallery,
        'aria-label',
        gallery.id === 'productSliderThumbs' ? 'Product image thumbnails' : 'Product image gallery',
      );

      const slides = [...gallery.querySelectorAll('.product__media-item, .main_SliderThumbs')].filter(
        (slide) => !slide.classList.contains('slick-cloned'),
      );
      slides.forEach((slide, index) => {
        setAttribute(slide, 'role', 'group');
        setAttribute(slide, 'aria-roledescription', 'slide');
        setAttribute(slide, 'aria-label', `${index + 1} of ${slides.length}`);
      });
    });

    const productTitle = textFrom(document.querySelector('h1')) || 'Product';
    document.querySelectorAll('a.lightbox-container[href]').forEach((link, index) => {
      if (link.getAttribute('aria-label') || textFrom(link)) return;
      const imageAlt = link.querySelector('img[alt]:not([alt=""])')?.getAttribute('alt')?.trim();
      setAttribute(link, 'aria-label', imageAlt || `Open ${productTitle} image ${index + 1}`);
    });
  };

  const fixJudgeMe = () => {
    document.querySelectorAll('.jdgm-rev-widg__summary-stars[role="img"]').forEach((summary) => {
      const summaryLabel = summary.getAttribute('aria-label') || 'Review rating';
      summary.querySelectorAll('a[href], button').forEach((control) => {
        if (!control.getAttribute('aria-label') && !textFrom(control)) {
          setAttribute(control, 'aria-label', summaryLabel);
        }
      });
      removeAttribute(summary, 'role');
      removeAttribute(summary, 'aria-label');
    });

    document.querySelectorAll('a.jdgm-cf-bar[href="#"]').forEach((bar) => {
      removeAttribute(bar, 'href');
      setAttribute(bar, 'tabindex', '-1');
      setAttribute(bar, 'role', 'presentation');
      setAttribute(bar, 'aria-hidden', 'true');
    });

    document.querySelectorAll('.jdgm-carousel-wrapper img:not([alt]), .jdgm-carousel-item img:not([alt])').forEach((image) => {
      const card = image.closest('.jdgm-carousel-item');
      const reviewer = textFrom(card?.querySelector('.jdgm-carousel-item__reviewer-name'));
      setAttribute(image, 'alt', reviewer ? `Customer review from ${reviewer}` : '');
    });
  };

  const fixPreProductCart = () => {
    document.querySelectorAll('img#pp-cart-header-logo:not([alt])').forEach((image) => setAttribute(image, 'alt', 'Lifely'));
    document.querySelectorAll('img#pp-cart-header-image:not([alt])').forEach((image) => setAttribute(image, 'alt', ''));
    document.querySelectorAll('img[id^="pp-cart-line-item-image-"]:not([alt])').forEach((image) => {
      const line = image.closest('.pp-cart-line-item');
      const title = textFrom(line?.querySelector('.pp-cart-line-item__product-name, .pp-cart-line-item__title'));
      setAttribute(image, 'alt', title ? `${title} product image` : 'Product image');
    });
    document.querySelectorAll('input[id^="pp-variantbutton-tally-"]').forEach((input) => {
      const line = input.closest('.pp-cart-line-item');
      const title = textFrom(line?.querySelector('.pp-cart-line-item__product-name, .pp-cart-line-item__title'));
      setAttribute(input, 'aria-label', title ? `Quantity for ${title}` : 'Product quantity');
    });
    document.querySelectorAll('select[id^="pp-variantfield-"]').forEach((select) => {
      const line = select.closest('.pp-cart-line-item');
      const title = textFrom(line?.querySelector('.pp-cart-line-item__product-name, .pp-cart-line-item__title'));
      setAttribute(select, 'aria-label', title ? `Variant for ${title}` : 'Product variant');
    });
  };

  const fixEmbeddedContent = () => {
    document.querySelectorAll('iframe:not([title]), iframe[title=""]').forEach((frame) => {
      const source = (frame.getAttribute('src') || '').toLowerCase();
      if (/gorgias|chat|messenger/.test(source)) setAttribute(frame, 'title', 'Customer support chat');
      else if (/klaviyo/.test(source)) setAttribute(frame, 'title', 'Email sign-up form');
      else if (/youtube|vimeo/.test(source)) setAttribute(frame, 'title', 'Product video');
    });
  };

  const fixKnownMissingImageAlternatives = () => {
    document.querySelectorAll('img:not([alt])').forEach((image) => {
      const source = (image.currentSrc || image.getAttribute('src') || '').toLowerCase();
      if (/stars_small\.png/.test(source)) {
        setAttribute(image, 'alt', '5 out of 5 stars');
      } else if (/group_1839_/.test(source) || image.closest('button[aria-label], [aria-hidden="true"]')) {
        setAttribute(image, 'alt', '');
      }
    });
  };

  const respectReducedMotion = () => {
    if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('video[autoplay]').forEach((video) => {
      video.pause();
      video.removeAttribute('autoplay');
      video.controls = true;
    });
  };

  const runFixes = () => {
    fixSortControls();
    fixPagination();
    fixNestedShopLinks();
    fixProductGalleries();
    fixJudgeMe();
    fixPreProductCart();
    fixEmbeddedContent();
    fixKnownMissingImageAlternatives();
    respectReducedMotion();
  };

  let queued = false;
  const scheduleFixes = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      runFixes();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleFixes, { once: true });
  } else {
    scheduleFixes();
  }

  document.addEventListener('shopify:section:load', scheduleFixes);
  window.addEventListener('pageshow', scheduleFixes);
  new MutationObserver(scheduleFixes).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'href', 'src', 'aria-hidden'],
  });
})();
